import { NPSResponseType, UserData } from '~/types/userTypes';
import { appApi } from '~/redux/api/appApiSlice';
import {
  SavedPropertiesResponseType,
  SavedPropertyFilterType,
} from '~/types/activitiesTypes';

export const userApiSlice = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<UserData, void>({
      query: () => ({
        url: 'api/v2/user',
      }),
      providesTags: ['user'],
    }),
    getUserPhones: builder.query<
      {
        phone: { number: string };
        id: number;
        whats_app: boolean;
        main: boolean;
      }[],
      void
    >({
      query: () => ({
        url: 'api/v2/user/phones',
      }),
      providesTags: ['userPhone'],
    }),
    addPhoneNumber: builder.mutation<
      void,
      { number: string; country_code: string }
    >({
      query: (body) => ({
        url: 'api/v2/user/phone/add',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['userPhone']),
    }),
    updatePhoneNumber: builder.mutation<
      void,
      {
        phoneId: number;
        data: { main?: boolean; is_verified?: boolean; whatsApp?: boolean };
      }
    >({
      query: ({ phoneId, data }) => ({
        url: `api/v2/user/update-user-phone/${phoneId}`,
        method: 'POST',
        body: {
          aqarmap_user_phone: data,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['userPhone']),
    }),
    removePhoneNumber: builder.mutation<void, number>({
      query: (phoneId: number) => ({
        url: `api/v2/user/remove-user-phone/${phoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['userPhone']),
    }),
    getFavoriteCount: builder.query<any, void>({
      query: () => 'api/v2/favourite/user/count',
    }),
    getNotesCount: builder.query<any, void>({
      query: () => 'api/v2/note/user/count',
    }),
    updateUserInfo: builder.mutation<any, FormData>({
      query: (formData) => {
        return {
          url: 'api/v2/user/update-profile-basic-info',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['user']),
    }),
    updateCompany: builder.mutation<any, FormData>({
      query: (formData) => {
        return {
          url: 'api/v2/user/update-company-info',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['user']),
    }),
    removePhoto: builder.mutation<any, FormData>({
      query: (formData) => {
        return {
          url: 'api/v2/user/remove-photo',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['user']),
    }),
    updatePersonalInfo: builder.mutation<any, FormData>({
      query: (formData) => {
        return {
          url: 'api/v2/user/update-profile-personal-info',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['user']),
    }),
    getSections: builder.query<any, void>({
      query: () => 'api/v4/sections',
    }),
    getAllPropertyType: builder.query({
      query: (query = '') => `api/v4/property-type/search?${query}`,
    }),
    userHasRentalPackage: builder.query({
      query: () => '/api/v4/user/has_rental_package',
    }),
    initializeListing: builder.mutation({
      query: (body) => ({
        url: '/api/v2/listing/initialize',
        method: 'POST',
        body: body,
      }),
    }),
    getLocations: builder.mutation({
      query: (searchParams) => `/api/v2/location/search?${searchParams}`,
    }),
    getListingChildren: builder.mutation({
      query: ({
        listingId,
        propertyType,
      }: {
        listingId: number;
        propertyType: number;
      }) => ({
        url: `/api/v2/listing/${listingId}/children?${new URLSearchParams({
          propertyType: String(propertyType),
        }).toString()}`,
        method: 'GET',
      }),
    }),
    cloneListing: builder.mutation({
      query: ({
        unitId,
        sectionId,
        propertyId,
      }: {
        unitId: number;
        sectionId: number;
        propertyId: number;
      }) => ({
        url: `/api/v2/listing/${unitId}/clone`,
        method: 'POST',
        header: {
          'Content-Type': 'application/json',
        },
        body: {
          section: sectionId,
          propertyType: propertyId,
        },
      }),
    }),
    getUserCards: builder.query<any, void>({
      query: () => '/api/v2/user/card',
      providesTags: ['userCards'],
    }),
    AddUserCard: builder.mutation<any, FormData>({
      query: (formData) => {
        return {
          url: 'api/v2/user/card/new',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['userCards']),
    }),
    deleteUserCard: builder.mutation<any, number>({
      query: (id) => {
        return {
          url: `api/v2/user/card/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['userCards']),
    }),
    getListingDetails: builder.query({
      query: (listingId) =>
        `/api/v4/listing/${listingId}?timestamp=${Date.now()}`,
      keepUnusedDataFor: 0, // Cache for 60 seconds
    }),
    uploadPhotos: builder.mutation({
      query: ({ formData, id }) => ({
        url: `/api/v2/listing/${id}/upload-photos`,
        method: 'POST',
        body: formData,
      }),
    }),
    deletePhotos: builder.mutation({
      query: ({ formData, id }) => ({
        url: `/api/v2/listing/${id}/delete-photos`,
        method: 'POST',
        body: formData,
      }),
    }),
    finalizeListing: builder.mutation({
      query: (id) => ({
        url: `/api/v2/listing/${id}/finalize`,
        method: 'POST',
      }),
    }),
    setMainPhoto: builder.mutation({
      query: ({ listingId, photoId }) => ({
        url: `/api/v2/listing/${listingId}/${photoId}/set-main`,
        method: 'GET',
      }),
    }),
    listingRules: builder.query({
      query: (listingId) => ({
        url: `/api/v2/listing/${listingId}/rules`,
      }),
    }),
    listingStatus: builder.query<
      {
        isEligibleForFreePublish: boolean;
        isPayAsYouGoAllowed: boolean;
        userAvailableCreditBalance: number;
      },
      string | undefined
    >({
      query: (listingId) => ({
        url: `/api/v2/listing/${listingId}/status`,
      }),
    }),
    payListingFees: builder.mutation({
      query: (listingId) => ({
        url: `/api/v2/listing/${listingId}/pay`,
        method: 'POST',
      }),
    }),
    transferListingToParent: builder.mutation({
      query: (listingId) => ({
        url: `/api/v4/listing/${listingId}/transfer-to-parent`,
        method: 'POST',
      }),
    }),
    getListingCustomFields: builder.query({
      query: () => '/api/v4/custom-field',
    }),
    addListingDetails: builder.mutation({
      query: ({ body, id }) => ({
        url: `/api/v2/listing/${id}/details`,
        method: 'POST',
        body: body,
      }),
    }),
    sortListingPhotos: builder.mutation({
      query: ({ body, listingId }) => ({
        url: `/api/v2/listing/${listingId}/photos/sort`,
        method: 'POST',
        body,
      }),
    }),
    generateListingDescription: builder.mutation({
      query: (listingId) => ({
        url: `/api/v2/listing/${listingId}/generated-description`,
        method: 'GET',
      }),
    }),
    getNPSData: builder.query<NPSResponseType, void>({
      query: () => ({
        url: '/api/v2/user/nps-data',
      }),
    }),
    addNote: builder.mutation<any, { listingId: number; note: string }>({
      query: (body) => {
        const formData = new FormData();
        formData.append('notes[0][listingId]', String(body.listingId));
        formData.append('notes[0][note]', body.note);

        return {
          url: '/api/v2/listing/note',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getSavedProperties: builder.query<
      {
        data: SavedPropertiesResponseType;
        headers: Headers;
      },
      SavedPropertyFilterType
    >({
      query: (query) => ({
        url: `/api/v4/user/favorites?includeNotes=${query.includeNotes}&sortBy=${query.sort}&sortDir=${query.direction}&page=${query.page}&limit=${query.limit}`,
      }),
      transformResponse: (response: SavedPropertiesResponseType, meta) => {
        return {
          data: response,
          headers:
            (meta as { response?: { headers?: Headers } })?.response?.headers ??
            new Headers(),
        };
      },
    }),
    deleteFavorite: builder.mutation<any, number>({
      query: (listingId) => ({
        url: `/api/v2/listing/${listingId}/favourite`,
        method: 'DELETE',
      }),
    }),
    addFavorite: builder.mutation<any, number[]>({
      query: (ids) => ({
        url: '/api/v2/favourite',
        method: 'POST',
        body: { listing: ids },
      }),
    }),
    getUserFavoritesList: builder.query<
      { hasNote: boolean; listing: { id: number } }[],
      void
    >({
      query: () => '/api/v4/user/favorites/list',
    }),
    deleteAllFavorites: builder.mutation({
      query: () => ({
        url: '/api/v4/user/favorites/delete-favorites',
        method: 'DELETE',
      }),
    }),
    deleteAllNotes: builder.mutation({
      query: () => ({
        url: '/api/v4/user/favorites/delete-notes',
        method: 'DELETE',
      }),
    }),
    getUserFavorites: builder.query<
      SavedPropertiesResponseType,
      {
        query: SavedPropertyFilterType;
        userId: string;
      }
    >({
      query: ({ query, userId }) => ({
        url: `/api/v4/user/${userId}/favorites?includeNotes=${query.includeNotes}&_expiration=${query._expiration}&_hash=${query._hash}&sortBy=${query.sort}&sortDir=${query.direction}&page=${query.page}&limit=${query.limit}`,
      }),
    }),
    getUserAccountById: builder.query({
      query: (id) => `/api/v2/user/${id}/account`,
    }),
    getRateValues: builder.mutation({
      query: () => '/api/v4/rate/values',
    }),
    addListingRate: builder.mutation({
      query: ({ listingId, ...rest }) => ({
        url: `/api/v4/listing/${listingId}/rate`,
        method: 'POST',
        body: rest,
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdateUserInfoMutation,
  useUpdateCompanyMutation,
  useRemovePhotoMutation,
  useUpdatePersonalInfoMutation,
  useGetFavoriteCountQuery,
  useGetNotesCountQuery,
  useGetUserPhonesQuery,
  useRemovePhoneNumberMutation,
  useUpdatePhoneNumberMutation,
  useAddPhoneNumberMutation,
  useGetSectionsQuery,
  useGetAllPropertyTypeQuery,
  useUserHasRentalPackageQuery,
  useInitializeListingMutation,
  useGetLocationsMutation,
  useGetListingChildrenMutation,
  useGetListingDetailsQuery,
  useGetUserCardsQuery,
  useDeleteUserCardMutation,
  useAddUserCardMutation,
  useUploadPhotosMutation,
  useDeletePhotosMutation,
  useFinalizeListingMutation,
  useSetMainPhotoMutation,
  useListingRulesQuery,
  useListingStatusQuery,
  usePayListingFeesMutation,
  useTransferListingToParentMutation,
  useAddListingDetailsMutation,
  useGetListingCustomFieldsQuery,
  useSortListingPhotosMutation,
  useGenerateListingDescriptionMutation,
  useLazyGetNPSDataQuery,
  useAddNoteMutation,
  useGetSavedPropertiesQuery,
  useDeleteFavoriteMutation,
  useAddFavoriteMutation,
  useLazyGetUserFavoritesListQuery,
  useDeleteAllFavoritesMutation,
  useDeleteAllNotesMutation,
  useGetUserFavoritesQuery,
  useGetUserAccountByIdQuery,
  useGetRateValuesMutation,
  useAddListingRateMutation,
  useCloneListingMutation,
} = userApiSlice;
